

import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { MwcSafeAny } from 'mwc/core/types';

import { MwcSelectItemInterface, MwcSelectModeType } from './select.types';

@Component({
  selector: 'mwc-option-container',
  exportAs: 'mwcOptionContainer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  template: `
    <div>
      <ng-content></ng-content>
      <div *ngIf="listOfContainerItem.length === 0" class="mwc-select-item-empty">
        <mwc-embed-empty mwcComponentName="select" [specificContent]="notFoundContent!"></mwc-embed-empty>
      </div>
      <cdk-virtual-scroll-viewport
        [class.full-width]="!matchWidth"
        [itemSize]="itemSize"
        [maxBufferPx]="itemSize * maxItemLength"
        [minBufferPx]="itemSize * maxItemLength"
        (scrolledIndexChange)="onScrolledIndexChange($event)"
        [style.height.px]="listOfContainerItem.length * itemSize"
        [style.max-height.px]="itemSize * maxItemLength"
      >
        <ng-template
          cdkVirtualFor
          [cdkVirtualForOf]="listOfContainerItem"
          [cdkVirtualForTrackBy]="trackValue"
          [cdkVirtualForTemplateCacheSize]="0"
          let-item
        >
          <ng-container [ngSwitch]="item.type">
            <mwc-option-item-group *ngSwitchCase="'group'" [mwcLabel]="item.groupLabel"></mwc-option-item-group>
            <mwc-option-item
              *ngSwitchCase="'item'"
              [icon]="menuItemSelectedIcon"
              [customContent]="item.mwcCustomContent"
              [template]="item.template"
              [grouped]="!!item.groupLabel"
              [disabled]="item.mwcDisabled"
              [showState]="mode === 'tags' || mode === 'multiple'"
              [label]="item.mwcLabel"
              [compareWith]="compareWith"
              [activatedValue]="activatedValue"
              [listOfSelectedValue]="listOfSelectedValue"
              [value]="item.mwcValue"
              (itemHover)="onItemHover($event)"
              (itemClick)="onItemClick($event)"
            ></mwc-option-item>
          </ng-container>
        </ng-template>
      </cdk-virtual-scroll-viewport>
      <ng-template [ngTemplateOutlet]="dropdownRender"></ng-template>
    </div>
  `,
  host: { class: 'mwc-select-dropdown' }
})
export class MwcOptionContainerComponent implements OnChanges, AfterViewInit {
  @Input() notFoundContent: string | TemplateRef<MwcSafeAny> | undefined = undefined;
  @Input() menuItemSelectedIcon: TemplateRef<MwcSafeAny> | null = null;
  @Input() dropdownRender: TemplateRef<MwcSafeAny> | null = null;
  @Input() activatedValue: MwcSafeAny | null = null;
  @Input() listOfSelectedValue: MwcSafeAny[] = [];
  @Input() compareWith!: (o1: MwcSafeAny, o2: MwcSafeAny) => boolean;
  @Input() mode: MwcSelectModeType = 'default';
  @Input() matchWidth = true;
  @Input() itemSize = 44;
  @Input() maxItemLength = 8;
  @Input() listOfContainerItem: MwcSelectItemInterface[] = [];
  @Output() readonly itemClick = new EventEmitter<MwcSafeAny>();
  @Output() readonly scrollToBottom = new EventEmitter<void>();
  @ViewChild(CdkVirtualScrollViewport, { static: true }) cdkVirtualScrollViewport!: CdkVirtualScrollViewport;
  private scrolledIndex = 0;

  constructor() {}

  onItemClick(value: MwcSafeAny): void {
    this.itemClick.emit(value);
  }

  onItemHover(value: MwcSafeAny): void {
    // TODO: keydown.enter won't activate this value
    this.activatedValue = value;
  }

  trackValue(_index: number, option: MwcSelectItemInterface): MwcSafeAny {
    return option.key;
  }

  onScrolledIndexChange(index: number): void {
    this.scrolledIndex = index;
    if (index === this.listOfContainerItem.length - this.maxItemLength) {
      this.scrollToBottom.emit();
    }
  }

  scrollToActivatedValue(): void {
    const index = this.listOfContainerItem.findIndex(item => this.compareWith(item.key, this.activatedValue));
    if (index < this.scrolledIndex || index >= this.scrolledIndex + this.maxItemLength) {
      this.cdkVirtualScrollViewport.scrollToIndex(index || 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { listOfContainerItem, activatedValue } = changes;
    if (listOfContainerItem || activatedValue) {
      this.scrollToActivatedValue();
    }
  }
  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToActivatedValue());
  }
}
